import React, { useEffect, useMemo, useRef, useState } from 'react';
import IapQrScanner from './IapQrScanner';
import IapDocumentViewer from './IapDocumentViewer';
import { deriveIapMetadata, incidentKeyFor, safeIapDisplayName } from './iapMetadata';
import {
  deleteSavedIapRecord,
  findSavedIapByDocumentUrl,
  listSavedIaps,
  writeSavedIap,
} from './iapOfflineStore';
import { downloadSelectedIap } from './iapDownload';
import {
  browseForIap,
  clearPendingIapSelection,
  consumePendingIapSelection,
  deleteNativeIap,
  localIapWebUrl,
  nativeIapAvailable,
  resolveNativeIapFile,
} from './nativeIap';
import './iap.css';

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

function likelyDirectDocument(urlValue) {
  try {
    const url = new URL(String(urlValue || ''));
    if (url.pathname.toLowerCase().endsWith('.pdf')) return true;
    if (['drive.google.com', 'docs.google.com'].includes(url.hostname.toLowerCase())) {
      return url.pathname.includes('/file/d/') || Boolean(url.searchParams.get('id'));
    }
  } catch (_) {}
  return false;
}

async function blobForRecord(record) {
  if (record?.blob instanceof Blob) return record.blob;
  const fileUri = record?.storage === 'native'
    ? await resolveNativeIapFile(record?.id, record?.fileUri)
    : String(record?.fileUri || '');
  if (!fileUri) throw new Error('The offline PDF file is missing from this saved IAP.');
  const localUrl = localIapWebUrl(fileUri);
  const response = await fetch(localUrl, { cache: 'no-store' });
  if (!response.ok) throw new Error('The offline PDF file could not be opened.');
  return response.blob();
}

function groupRecords(records) {
  const groups = new Map();
  (records || []).forEach(record => {
    const key = record.incidentKey || incidentKeyFor(record);
    const row = groups.get(key) || {
      key,
      incidentName: record.incidentName || 'Incident',
      incidentOrder: record.incidentOrder || '',
      records: [],
    };
    row.records.push(record);
    groups.set(key, row);
  });
  return [...groups.values()].map(group => ({
    ...group,
    records: group.records.sort((a, b) => String(b.savedAt || '').localeCompare(String(a.savedAt || ''))),
  })).sort((a, b) => String(b.records[0]?.savedAt || '').localeCompare(String(a.records[0]?.savedAt || '')));
}

function PendingSave({ pending, onChange, onSave, onCancel, busy }) {
  const searchable = (pending?.textPages || []).some(page => String(page || '').trim());
  return (
    <section className="iap-card">
      <div className="iap-section-head">
        <div>
          <span className="iap-kicker">OFFLINE COPY READY</span>
          <h3>Confirm this IAP</h3>
          <p>Line Medic extracted what it could from the PDF. Fix anything that is wrong before saving.</p>
        </div>
      </div>

      <div className="iap-form">
        <label>Incident name
          <input value={pending.incidentName} onChange={event => onChange('incidentName', event.target.value)} placeholder="Timber" />
        </label>
        <label>Incident order
          <input value={pending.incidentOrder} onChange={event => onChange('incidentOrder', event.target.value)} placeholder="CA-LPF-002271" autoCapitalize="characters" />
        </label>
        <label>Operational period
          <input value={pending.operationalPeriod} onChange={event => onChange('operationalPeriod', event.target.value)} placeholder="9/23/2026 0700 – 9/24/2026 0700" />
        </label>
      </div>

      <div className="iap-document-summary">
        <strong>{pending.filename || 'Incident-IAP.pdf'}</strong>
        <span>{pending.pageCount || '—'} pages{pending.bytes ? ` · ${formatBytes(pending.bytes)}` : ''}</span>
        <span>{searchable ? 'Searchable text found' : 'No searchable text layer found'}</span>
        {pending.textTruncated && <span>Search index limited for this unusually large PDF</span>}
      </div>

      <div className="iap-actions">
        <button type="button" className="iap-primary-btn" disabled={busy || !pending.incidentName.trim()} onClick={onSave}>
          {busy ? 'Saving…' : 'Save Offline'}
        </button>
        <button type="button" className="iap-secondary-btn" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

function WebBrowseFallback({ sourceUrl, onChoose, onCancel }) {
  const [documentUrl, setDocumentUrl] = useState(likelyDirectDocument(sourceUrl) ? sourceUrl : '');
  return (
    <section className="iap-card">
      <span className="iap-kicker">WEB PREVIEW FALLBACK</span>
      <h3>Open the IAP source</h3>
      <p>The installed iPhone app has an in-app folder browser. In a web preview, open the source in another tab, click to the actual IAP PDF, then paste that PDF/Drive file link here.</p>
      <div className="iap-actions">
        <a className="iap-secondary-btn iap-link-button" href={sourceUrl} target="_blank" rel="noreferrer">Open Source ↗</a>
      </div>
      <div className="iap-manual-row">
        <input type="url" value={documentUrl} onChange={event => setDocumentUrl(event.target.value)} placeholder="Paste the selected IAP PDF / Drive file link" />
        <button type="button" className="iap-primary-btn" disabled={!documentUrl.trim()} onClick={() => onChoose(documentUrl.trim())}>Use this IAP</button>
      </div>
      <button type="button" className="iap-text-btn" onClick={onCancel}>Cancel</button>
    </section>
  );
}

export default function SavedIaps() {
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('library');
  const [sourceUrl, setSourceUrl] = useState('');
  const [pending, setPending] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState('');
  const [viewerRecord, setViewerRecord] = useState(null);
  const [viewerBlob, setViewerBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const chooseDocumentRef = useRef(null);

  const refresh = async () => {
    const next = await listSavedIaps().catch(err => {
      setError(err?.message || 'Unable to load saved IAPs.');
      return [];
    });
    setRecords(next);
    setStatus('ready');
    return next;
  };

  useEffect(() => { refresh(); }, []);

  const groups = useMemo(() => groupRecords(records), [records]);
  const selectedGroup = groups.find(group => group.key === selectedIncident) || null;

  const cleanupPendingFile = async value => {
    if (value?.storage === 'native' && value?.id) {
      await deleteNativeIap(value.id).catch(() => {});
    }
  };

  const chooseDocument = async (documentUrl, originalSource = sourceUrl) => {
    setError('');
    setBusy(true);
    try {
      const existing = await findSavedIapByDocumentUrl(documentUrl);
      if (existing) {
        setSelectedIncident(existing.incidentKey || incidentKeyFor(existing));
        setMode('incident');
        setError('That exact IAP is already saved offline.');
        return;
      }

      const downloaded = await downloadSelectedIap(documentUrl);
      const metadata = deriveIapMetadata({
        sourceUrl: originalSource,
        documentUrl,
        filename: downloaded.filename,
        textPages: downloaded.textPages,
      });
      setPending({
        ...downloaded,
        sourceUrl: originalSource,
        documentUrl,
        incidentName: metadata.incidentName,
        incidentOrder: metadata.incidentOrder,
        operationalPeriod: metadata.operationalPeriod,
      });
      setMode('confirm');
      // The native handoff survives WebView/auth remounts. Clear it only after
      // this screen has a complete downloaded document ready for confirmation.
      await clearPendingIapSelection().catch(() => {});
    } catch (err) {
      setError(err?.message || 'Unable to save that IAP.');
      setMode(nativeIapAvailable() ? 'library' : 'web-browse');
    } finally {
      setBusy(false);
    }
  };
  chooseDocumentRef.current = chooseDocument;

  useEffect(() => {
    let cancelled = false;
    if (!nativeIapAvailable()) return undefined;

    consumePendingIapSelection()
      .then(selection => {
        if (cancelled || !selection?.url) return;
        const originalSource = selection.sourceUrl || selection.url;
        setSourceUrl(originalSource);
        chooseDocumentRef.current?.(selection.url, originalSource);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, []);

  const browseSource = async nextSource => {
    const value = String(nextSource || '').trim();
    if (!value) return;
    setSourceUrl(value);
    setError('');

    if (!nativeIapAvailable()) {
      if (likelyDirectDocument(value)) {
        await chooseDocument(value, value);
      } else {
        setMode('web-browse');
      }
      return;
    }

    setBusy(true);
    try {
      const selected = await browseForIap(value);
      if (!selected) {
        setMode('library');
        return;
      }
      await chooseDocument(selected, value);
    } catch (err) {
      setError(err?.message || 'Unable to browse this IAP source.');
      setMode('library');
    } finally {
      setBusy(false);
    }
  };

  const startScan = () => {
    setError('');
    setMode('scan');
  };

  const updatePending = (key, value) => setPending(current => ({ ...current, [key]: value }));

  const savePending = async () => {
    if (!pending?.incidentName?.trim()) return;
    setBusy(true);
    setError('');
    try {
      const record = {
        ...pending,
        incidentName: pending.incidentName.trim(),
        incidentOrder: pending.incidentOrder.trim().toUpperCase(),
        operationalPeriod: pending.operationalPeriod.trim(),
        incidentKey: incidentKeyFor(pending),
        savedAt: new Date().toISOString(),
        searchable: (pending.textPages || []).some(page => String(page || '').trim()),
      };
      await writeSavedIap(record);
      setPending(null);
      await refresh();
      setSelectedIncident(record.incidentKey);
      setMode('incident');
    } catch (err) {
      setError(err?.message || 'Unable to finish saving this IAP.');
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    const current = pending;
    setPending(null);
    setMode('library');
    await cleanupPendingFile(current);
  };

  const openRecord = async record => {
    setBusy(true);
    setError('');
    try {
      const blob = await blobForRecord(record);
      setViewerRecord(record);
      setViewerBlob(blob);
      setMode('viewer');
    } catch (err) {
      setError(err?.message || 'Unable to open this offline IAP.');
    } finally {
      setBusy(false);
    }
  };

  const deleteRecord = async record => {
    if (!window.confirm(`Delete ${safeIapDisplayName(record)} from this device?`)) return;
    setBusy(true);
    try {
      if (record.storage === 'native' && record.id) await deleteNativeIap(record.id).catch(() => {});
      await deleteSavedIapRecord(record.id);
      const next = await refresh();
      const stillExists = next.some(item => (item.incidentKey || incidentKeyFor(item)) === (record.incidentKey || incidentKeyFor(record)));
      if (!stillExists) {
        setSelectedIncident('');
        setMode('library');
      }
    } catch (err) {
      setError(err?.message || 'Unable to delete this saved IAP.');
    } finally {
      setBusy(false);
    }
  };

  const deleteIncident = async group => {
    if (!window.confirm(`Remove ${group.incidentName} and all ${group.records.length} saved IAP${group.records.length === 1 ? '' : 's'} from this device?`)) return;
    setBusy(true);
    try {
      for (const record of group.records) {
        if (record.storage === 'native' && record.id) await deleteNativeIap(record.id).catch(() => {});
        await deleteSavedIapRecord(record.id);
      }
      await refresh();
      setSelectedIncident('');
      setMode('library');
    } catch (err) {
      setError(err?.message || 'Unable to remove this incident.');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'scan') {
    return (
      <div className="iap-root">
        <IapQrScanner
          onCancel={() => setMode('library')}
          onScan={url => browseSource(url)}
        />
        {error && <div className="iap-alert">{error}</div>}
      </div>
    );
  }

  if (mode === 'web-browse') {
    return (
      <div className="iap-root">
        <WebBrowseFallback
          sourceUrl={sourceUrl}
          onChoose={url => chooseDocument(url, sourceUrl)}
          onCancel={() => setMode('library')}
        />
        {error && <div className="iap-alert">{error}</div>}
      </div>
    );
  }

  if (mode === 'confirm' && pending) {
    return (
      <div className="iap-root">
        <PendingSave
          pending={pending}
          onChange={updatePending}
          onSave={savePending}
          onCancel={cancelPending}
          busy={busy}
        />
        {error && <div className="iap-alert">{error}</div>}
      </div>
    );
  }

  if (mode === 'viewer' && viewerRecord) {
    return (
      <div className="iap-root">
        <div className="iap-viewer-head">
          <div>
            <span className="iap-kicker">SAVED OFFLINE</span>
            <h3>{viewerRecord.incidentName}</h3>
            <p>{viewerRecord.operationalPeriod || viewerRecord.filename}{viewerRecord.incidentOrder ? ` · ${viewerRecord.incidentOrder}` : ''}</p>
          </div>
          <button type="button" className="iap-secondary-btn" onClick={() => {
            setViewerBlob(null);
            setViewerRecord(null);
            setMode(selectedIncident ? 'incident' : 'library');
          }}>← IAP List</button>
        </div>
        {viewerBlob && <IapDocumentViewer blob={viewerBlob} title={safeIapDisplayName(viewerRecord)} onError={message => setError(message)} />}
        {error && <div className="iap-alert">{error}</div>}
      </div>
    );
  }

  if (mode === 'incident' && selectedGroup) {
    const source = selectedGroup.records.find(record => record.sourceUrl)?.sourceUrl || '';
    return (
      <div className="iap-root">
        <div className="iap-section-head">
          <div>
            <span className="iap-kicker">SAVED INCIDENT</span>
            <h3>{selectedGroup.incidentName}</h3>
            <p>{selectedGroup.incidentOrder || 'Incident IAP library'} · {selectedGroup.records.length} saved operational period{selectedGroup.records.length === 1 ? '' : 's'}</p>
          </div>
          <button type="button" className="iap-secondary-btn" onClick={() => { setSelectedIncident(''); setMode('library'); }}>← Incidents</button>
        </div>

        <div className="iap-actions">
          {source && <button type="button" className="iap-primary-btn" disabled={busy || navigator.onLine === false} onClick={() => browseSource(source)}>Add New IAP from Saved Source</button>}
          <button type="button" className="iap-secondary-btn" onClick={startScan}>Scan Another QR</button>
        </div>

        <div className="iap-list">
          {selectedGroup.records.map(record => (
            <article className="iap-record" key={record.id}>
              <button type="button" className="iap-record-open" disabled={busy} onClick={() => openRecord(record)}>
                <strong>{record.operationalPeriod || record.filename || 'Saved IAP'}</strong>
                <span>{record.pageCount || '—'} pages{record.bytes ? ` · ${formatBytes(record.bytes)}` : ''}</span>
                <span className="iap-offline-badge">Saved Offline</span>
                <small>{record.searchable ? 'Text search available' : 'View only · no searchable text layer'}</small>
              </button>
              <button type="button" className="iap-delete-btn" disabled={busy} onClick={() => deleteRecord(record)}>Delete</button>
            </article>
          ))}
        </div>

        <button type="button" className="iap-danger-btn" disabled={busy} onClick={() => deleteIncident(selectedGroup)}>Remove Incident</button>
        {error && <div className="iap-alert">{error}</div>}
      </div>
    );
  }

  return (
    <div className="iap-root">
      <div className="iap-hero">
        <div>
          <span className="iap-kicker">FIELD REFERENCE · OFFLINE</span>
          <h3>Saved IAPs</h3>
          <p>Scan the physical IAP QR, click through the incident's folders to the PDF you want, then save an offline copy. Line Medic does not guess which operational period is current.</p>
        </div>
        <button type="button" className="iap-primary-btn" disabled={busy || navigator.onLine === false} onClick={startScan}>Scan IAP QR</button>
      </div>

      <div className="iap-scope-note">
        <strong>Manual by design.</strong> You choose the actual IAP PDF each operational period. Saved copies stay on this device until you delete them.
      </div>

      {status === 'loading' && <div className="iap-empty">Loading saved IAPs…</div>}
      {status === 'ready' && !groups.length && (
        <div className="iap-empty">
          <strong>No IAPs saved yet.</strong>
          <span>Scan an incident's physical IAP QR while you have service. Once the PDF is saved, you can open and search it offline.</span>
        </div>
      )}

      <div className="iap-incident-grid">
        {groups.map(group => {
          const newest = group.records[0];
          return (
            <button type="button" className="iap-incident-card" key={group.key} onClick={() => {
              setSelectedIncident(group.key);
              setMode('incident');
            }}>
              <span className="iap-offline-badge">{group.records.length} OFFLINE</span>
              <strong>{group.incidentName}</strong>
              <small>{group.incidentOrder || 'Incident IAP'}</small>
              <span>{newest?.operationalPeriod || newest?.filename || 'Saved IAP'}</span>
            </button>
          );
        })}
      </div>

      {navigator.onLine === false && <div className="iap-offline-note">Offline now · saved IAPs remain available. Scanning/browsing a new source requires service.</div>}
      {error && <div className="iap-alert">{error}</div>}
    </div>
  );
}
